import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { toast } from 'react-toastify';
import toastConfig from '../../const/toastConfig';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';

const LEAVE_TYPES = [
  { value: 'ANNUAL', label: '연차' },
  { value: 'HALF_AM', label: '반차 (오전)' },
  { value: 'HALF_PM', label: '반차 (오후)' },
  { value: 'PUBLIC', label: '공가' },
  { value: 'SICK', label: '병가' },
  { value: 'SUMMER', label: '하계휴가' },
  { value: 'OTHER', label: '기타' },
];

const DOC_FORMS = [
  {
    id: 'vacation', title: '휴가 신청서', desc: '연차·반차·공가 등 휴가를 신청합니다.', active: true,
    icon: (color) => (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4M16 2v4" /><rect x="3" y="5" width="18" height="16" rx="2.5" /><path d="M3 10h18" /><path d="M8.5 15l2 2 4-4" /></svg>)
  },
  {
    id: 'trip', title: '출장 신청서', desc: '국내·해외 출장 계획과 경비를 신청합니다.', active: false,
    icon: (color) => (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8l9-5 9 5" /><path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" /><path d="M10 13h4" /></svg>)
  },
  {
    id: 'expense', title: '지출 결의서', desc: '법인카드 사용 내역 및 경비 정산을 요청합니다.', active: false,
    icon: (color) => (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" /><path d="M7 15h3" /></svg>)
  },
  {
    id: 'purchase', title: '구매 요청서', desc: '비품·소프트웨어 등 구매 결재를 요청합니다.', active: false,
    icon: (color) => (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2l-1 4h14l-1-4" /><path d="M5 6l-2 14h18L19 6" /><path d="M9 10v4M15 10v4" /></svg>)
  },
];

const eaFocusOn = (e) => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`; };
const eaFocusOff = (e) => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; };

function ApproverChip({ badge, name, locked, onRemove, onMoveLeft, onMoveRight, canMoveLeft, canMoveRight }) {
  if (locked) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 8px 5px 5px', background: '#f4f3f9', border: '1.5px solid #ececf2', borderRadius: 8, fontSize: 12.5, color: '#1f1d2e', height: 28, boxSizing: 'border-box' }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#8b8a99', background: 'white', padding: '2px 6px', borderRadius: 5 }}>{badge}</span>
        <span style={{ fontWeight: 600 }}>{name}</span>
      </span>
    );
  }

  return (
    <span style={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '8px 12px 10px',
      background: 'white',
      border: `1.5px solid ${hexToRgba(PURPLE, .25)}`,
      borderRadius: 10,
      fontSize: 12.5,
      color: '#1f1d2e',
      position: 'relative',
      minWidth: 90,
      boxShadow: '0 2px 6px rgba(100, 95, 136, 0.05)',
      boxSizing: 'border-box',
    }}>
      {/* Delete button in top-right corner */}
      <button type="button" onClick={onRemove} title="삭제" style={{
        position: 'absolute',
        top: -6,
        right: -6,
        width: 17,
        height: 17,
        borderRadius: '50%',
        background: '#e35d6a',
        border: 'none',
        cursor: 'pointer',
        color: 'white',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 4px rgba(227, 93, 106, 0.3)',
        transition: 'transform 120ms',
      }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
      </button>

      {/* Badge at the top */}
      <span style={{
        fontSize: 10,
        fontWeight: 700,
        color: PURPLE,
        background: hexToRgba(PURPLE, .12),
        padding: '2px 6px',
        borderRadius: 4,
        marginBottom: 5,
      }}>{badge}</span>

      {/* Name in middle */}
      <span style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{name}</span>

      {/* Reorder controls at the bottom */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <button type="button" onClick={onMoveLeft} disabled={!canMoveLeft} title="순서 앞으로" style={{
          width: 24,
          height: 18,
          borderRadius: 4,
          background: canMoveLeft ? hexToRgba(PURPLE, .06) : '#f4f3f9',
          border: `1px solid ${canMoveLeft ? hexToRgba(PURPLE, .2) : '#ececf2'}`,
          cursor: canMoveLeft ? 'pointer' : 'default',
          color: canMoveLeft ? PURPLE : '#aaa9b7',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 120ms',
        }}
          onMouseEnter={e => { if (canMoveLeft) { e.currentTarget.style.background = PURPLE; e.currentTarget.style.color = 'white'; } }}
          onMouseLeave={e => { if (canMoveLeft) { e.currentTarget.style.background = hexToRgba(PURPLE, .06); e.currentTarget.style.color = PURPLE; } }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>

        <button type="button" onClick={onMoveRight} disabled={!canMoveRight} title="순서 뒤로" style={{
          width: 24,
          height: 18,
          borderRadius: 4,
          background: canMoveRight ? hexToRgba(PURPLE, .06) : '#f4f3f9',
          border: `1px solid ${canMoveRight ? hexToRgba(PURPLE, .2) : '#ececf2'}`,
          cursor: canMoveRight ? 'pointer' : 'default',
          color: canMoveRight ? PURPLE : '#aaa9b7',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 120ms',
        }}
          onMouseEnter={e => { if (canMoveRight) { e.currentTarget.style.background = PURPLE; e.currentTarget.style.color = 'white'; } }}
          onMouseLeave={e => { if (canMoveRight) { e.currentTarget.style.background = hexToRgba(PURPLE, .06); e.currentTarget.style.color = PURPLE; } }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      </span>
    </span>
  );
}

function EACheckbox({ checked, onChange, label }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 13, color: '#3a3849', fontWeight: 500 }}>
      <span style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${checked ? PURPLE : '#d4d3df'}`, background: checked ? PURPLE : 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 140ms' }}>
        {checked && <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-6" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      </span>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} style={{ display: 'none' }} />
      <span>{label}</span>
    </label>
  );
}

function NotifRow({ who, options, notif, setNotif, accent, bgTint }) {
  return (
    <div>
      <div style={{ padding: '8px 14px', background: bgTint, fontSize: 11.5, fontWeight: 700, color: accent, letterSpacing: '.02em', borderBottom: '1px solid #ececf2' }}>{who}</div>
      <div style={{ padding: '12px 14px', display: 'flex', gap: 24, flexWrap: 'wrap', background: 'white' }}>
        {options.map(opt => <EACheckbox key={opt.key} checked={notif[opt.key]} onChange={v => setNotif({ ...notif, [opt.key]: v })} label={opt.label} />)}
      </div>
    </div>
  );
}

export default function ApprovalWritePage() {
  const { user } = useTokenStore();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [approverSearch, setApproverSearch] = useState('');
  const [showApproverList, setShowApproverList] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [showAgentList, setShowAgentList] = useState(false);
  const [holidays, setHolidays] = useState(new Set());
  const approverDropRef = useRef(null);
  const agentDropRef = useRef(null);

  const initForm = (orgCode) => ({
    orgCode: orgCode || user?.currentOrganizationCode || '',
    leaveType: 'ANNUAL', startDt: '', endDt: '', reason: '', remark: '',
    agentEmail: '', emergencyContact: '', emergencyRelation: '',
    notiApprovalStatus: true, notiDayBefore: false, notiApprover: true,
  });
  const [form, setForm] = useState(() => initForm());

  useEffect(() => {
    if (!user?.currentOrganizationCode) return;
    let cancelled = false;
    compatApi.post('/org/find/orgUser', {
      organizationCode: user.currentOrganizationCode,
      searchWord: '', searchOption: 'name',
    }).then(res => {
      if (!cancelled) setMembers((res.data || []).filter(m => m.userEmail !== user.userEmail));
    }).catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [user?.currentOrganizationCode, user?.userEmail]);

  useEffect(() => {
    if (!form.startDt || !form.endDt || form.endDt < form.startDt) { setHolidays(new Set()); return; }
    let cancelled = false;
    compatApi.get('/calendar/holidays', { params: { startDt: form.startDt, endDt: form.endDt } })
      .then(res => { if (!cancelled) setHolidays(new Set(res.data || [])); })
      .catch(() => { if (!cancelled) setHolidays(new Set()); });
    return () => { cancelled = true; };
  }, [form.startDt, form.endDt]);

  useEffect(() => {
    const handle = (e) => {
      if (approverDropRef.current && !approverDropRef.current.contains(e.target)) setShowApproverList(false);
      if (agentDropRef.current && !agentDropRef.current.contains(e.target)) setShowAgentList(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filteredAgents = useMemo(() => {
    const kw = agentSearch.trim().toLowerCase();
    if (!kw) return members;
    return members.filter(m =>
      (m.userName || '').toLowerCase().includes(kw) || m.userEmail.toLowerCase().includes(kw)
    );
  }, [members, agentSearch]);

  const filteredMembers = useMemo(() => {
    const added = new Set(approvers.map(a => a.userEmail));
    const kw = approverSearch.trim().toLowerCase();
    return members.filter(m =>
      !added.has(m.userEmail) &&
      (kw === '' || (m.userName || '').toLowerCase().includes(kw) || m.userEmail.toLowerCase().includes(kw))
    );
  }, [members, approvers, approverSearch]);

  const openModal = () => {
    setForm(initForm(user?.currentOrganizationCode));
    setApprovers([]); setApproverSearch(''); setShowApproverList(false);
    setAgentSearch(''); setShowAgentList(false);
    setShowModal(true);
  };

  const handleOrgChange = (newOrgCode) => {
    setForm(f => ({ ...f, orgCode: newOrgCode, agentEmail: '' }));
    setApprovers([]); setApproverSearch(''); setShowApproverList(false);
    setAgentSearch(''); setShowAgentList(false);
    compatApi.post('/org/find/orgUser', {
      organizationCode: newOrgCode, searchWord: '', searchOption: 'name',
    }).then(res => setMembers((res.data || []).filter(m => m.userEmail !== user?.userEmail)))
      .catch(() => setMembers([]));
  };

  const addApprover = (member) => {
    if (approvers.some(a => a.userEmail === member.userEmail)) {
      toast.warn('이미 추가된 결재자입니다.', toastConfig); return;
    }
    setApprovers(prev => [...prev, { userEmail: member.userEmail, userName: member.userName }]);
    setApproverSearch(''); setShowApproverList(false);
  };
  const removeApprover = (email) => setApprovers(prev => prev.filter(a => a.userEmail !== email));
  const moveApprover = (index, direction) => {
    const next = index + direction;
    if (next < 0 || next >= approvers.length) return;
    setApprovers(prev => { const arr = [...prev];[arr[index], arr[next]] = [arr[next], arr[index]]; return arr; });
  };

  const buildPayload = (status) => ({
    organizationCode: form.orgCode,
    leaveType: form.leaveType,
    startDt: form.startDt,
    endDt: form.endDt,
    reason: form.reason,
    remark: form.remark || null,
    agentEmail: form.agentEmail || null,
    emergencyContact: form.emergencyContact || null,
    emergencyRelation: form.emergencyRelation || null,
    approvers: approvers.map((a, i) => ({ userEmail: a.userEmail, order: i + 1 })),
    notiApprovalStatus: form.notiApprovalStatus,
    notiDayBefore: form.notiDayBefore,
    notiApprover: form.notiApprover,
    status,
  });

  const handleSave = async () => {
    if (!form.startDt || !form.endDt) { toast.warn('시작일과 종료일을 입력하세요.', toastConfig); return; }
    setSaving(true);
    try {
      await compatApi.post('/approval/vacation', buildPayload('DRAFT'));
      toast.success('임시 저장되었습니다.', toastConfig);
      setShowModal(false);
    } catch (e) { toast.error(e?.response?.data?.message || '저장에 실패했습니다.', toastConfig); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!form.startDt || !form.endDt) { toast.warn('시작일과 종료일을 입력하세요.', toastConfig); return; }
    if (!form.reason.trim()) { toast.warn('사유를 입력하세요.', toastConfig); return; }
    if (approvers.length === 0) { toast.warn('결재자를 한 명 이상 추가하세요.', toastConfig); return; }
    if (!window.confirm('제출 후에는 문서를 수정하거나 취소할 수 없습니다.\n제출하시겠습니까?')) return;
    setSubmitting(true);
    try {
      await compatApi.post('/approval/vacation', buildPayload('PENDING'));
      toast.success('휴가 신청서가 제출되었습니다.', toastConfig);
      setShowModal(false);
    } catch (e) { toast.error(e?.response?.data?.message || '제출에 실패했습니다.', toastConfig); }
    finally { setSubmitting(false); }
  };

  const workingDays = (() => {
    if (!form.startDt || !form.endDt) return null;
    const [sy, sm, sd] = form.startDt.split('-').map(Number);
    const [ey, em, ed] = form.endDt.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    if (end < start) return -1;
    let count = 0;
    const cur = new Date(start);
    while (cur <= end) {
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6) {
        const ds = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
        if (!holidays.has(ds)) count++;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  })();

  const memberDropItem = (m, onClick) => (
    <button key={m.userEmail} type="button" onMouseDown={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', borderRadius: 7, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
      onMouseEnter={e => e.currentTarget.style.background = '#fafaff'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`, color: 'white', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{(m.userName || '?').slice(-2)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f1d2e' }}>{m.userName}</div>
        <div style={{ fontSize: 11.5, color: '#aaa9b7' }}>{m.userEmail}</div>
      </div>
    </button>
  );

  const notiForm = { appStatus: form.notiApprovalStatus, appDayBefore: form.notiDayBefore, orgApprover: form.notiApprover };
  const setNotiForm = (updated) => setForm(f => ({ ...f, notiApprovalStatus: updated.appStatus, notiDayBefore: updated.appDayBefore, notiApprover: updated.orgApprover }));

  return (
    <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
      <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>
        {/* Breadcrumb + header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
            <span>전자결재</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}><path d="M6 9l6 6 6-6" /></svg>
            <span style={{ color: '#3a3849', fontWeight: 500 }}>기안 작성</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>기안 작성</h1>
              <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>결재 유형을 선택해 새로운 기안을 작성하세요.</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => navigate('/approval/draft-box')} style={{ height: 38, padding: '0 16px', borderRadius: 10, background: 'white', border: '1px solid #ececf2', color: '#3a3849', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9h10M7 13h10M7 17h6" /></svg>
                내 기안함
              </button>
              <button onClick={() => navigate('/approval/inbox')} style={{ height: 38, padding: '0 16px', borderRadius: 10, background: 'white', border: '1px solid #ececf2', color: '#3a3849', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                결재함
              </button>
            </div>
          </div>
        </div>

        {/* Cards section */}
        <section style={{ background: 'white', borderRadius: 16, border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)', padding: '24px 26px 30px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1f1d2e' }}>결재 양식</h2>
              <span style={{ fontSize: 11, fontWeight: 600, background: hexToRgba(PURPLE, .12), color: PURPLE, padding: '2px 8px', borderRadius: 999, lineHeight: 1.45 }}>{DOC_FORMS.length}</span>
            </div>
            <span style={{ fontSize: 11.5, color: '#aaa9b7' }}>현재 사용 가능: 1건</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {DOC_FORMS.map(f => {
              const disabled = !f.active;
              return (
                <button key={f.id} type="button" onClick={f.active ? openModal : undefined} disabled={disabled}
                  style={{ position: 'relative', background: disabled ? '#fafaff' : 'white', border: '1.5px solid #ececf2', borderRadius: 14, padding: '22px 20px 20px', textAlign: 'left', cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 160ms cubic-bezier(.2,.8,.2,1)', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 142 }}
                  onMouseEnter={e => { if (disabled) return; e.currentTarget.style.borderColor = hexToRgba(PURPLE, .4); e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 14px 26px -16px ${hexToRgba(PURPLE, .5)}`; }}
                  onMouseLeave={e => { if (disabled) return; e.currentTarget.style.borderColor = '#ececf2'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                >
                  {disabled && <span style={{ position: 'absolute', top: 12, right: 12, fontSize: 10.5, fontWeight: 600, color: '#aaa9b7', background: '#f4f3f9', padding: '3px 8px', borderRadius: 999 }}>준비 중</span>}
                  <div style={{ width: 52, height: 52, borderRadius: 13, background: disabled ? '#f0eff5' : `linear-gradient(135deg, ${hexToRgba(PURPLE, .14)}, ${hexToRgba(MINT, .18)})`, color: disabled ? '#c7c5d2' : PURPLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {f.icon(disabled ? '#c7c5d2' : PURPLE)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: disabled ? '#8b8a99' : '#1f1d2e', marginBottom: 4, letterSpacing: '-0.01em' }}>{f.title}</div>
                    <div style={{ fontSize: 12.5, color: disabled ? '#aaa9b7' : '#7a7989', lineHeight: 1.5 }}>{f.desc}</div>
                  </div>
                  {!disabled && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, fontWeight: 600, color: PURPLE }}>
                      작성하기
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}><path d="M6 9l6 6 6-6" /></svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Helper strip */}
        <div style={{ padding: '14px 18px', background: 'white', borderRadius: 12, border: '1px dashed #d4d3df', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: hexToRgba(MINT, .15), color: MINT, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5l3 2" /></svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1f1d2e' }}>추가 양식이 곧 제공됩니다</div>
              <div style={{ fontSize: 11.5, color: '#8b8a99', marginTop: 2 }}>출장·지출 결의·구매 요청 등 표준 양식은 순차적으로 오픈 예정입니다.</div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(28,26,46,.45)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)' }}>
          <div onClick={() => setShowModal(false)} style={{ position: 'absolute', inset: 0 }} />
          <div role="dialog" style={{ position: 'relative', zIndex: 1, width: 'min(720px, 100%)', maxHeight: 'calc(100vh - 48px)', background: 'white', borderRadius: 18, boxShadow: '0 30px 60px -20px rgba(20,15,40,.4)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #ececf2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: `linear-gradient(135deg, ${hexToRgba(PURPLE, .15)}, ${hexToRgba(MINT, .18)})`, color: PURPLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M9 13h6M9 17h4" /></svg>
                </div>
                <div>
                  <div style={{ fontSize: 15.5, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>휴가 신청서</div>
                  <div style={{ fontSize: 11.5, color: '#8b8a99', marginTop: 2 }}>필수 항목을 작성하고 결재를 요청하세요.</div>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} style={{ width: 34, height: 34, borderRadius: 9, background: 'transparent', border: 'none', cursor: 'pointer', color: '#8b8a99', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f4f3f9'; e.currentTarget.style.color = '#1f1d2e'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#8b8a99'; }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px 8px', background: '#fafaff' }}>
              <div style={{ background: 'white', borderRadius: 14, padding: 22, border: '1px solid #ececf2' }}>

                {/* 조직 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>조직 <span style={{ color: '#e35d6a' }}>*</span></label>
                  <select value={form.orgCode} onChange={e => handleOrgChange(e.target.value)}
                    style={{ width: '100%', height: 40, padding: '0 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 140ms, box-shadow 140ms' }}
                    onFocus={eaFocusOn} onBlur={eaFocusOff}>
                    {(user?.orgUserList || []).filter(org => org.isApproved).map(org => (
                      <option key={org.organizationCode} value={org.organizationCode}>{org.organizationName} ({org.organizationCode})</option>
                    ))}
                  </select>
                </div>

                {/* 결재선 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>결재선 <span style={{ color: '#e35d6a' }}>*</span></label>
                  <div style={{ minHeight: 52, padding: '8px 10px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    <ApproverChip badge="기안자" name={user?.userName || '-'} locked />
                    {approvers.map((a, i) => (
                      <ApproverChip
                        key={a.userEmail}
                        badge={`결재 ${i + 1}`}
                        name={a.userName}
                        onRemove={() => removeApprover(a.userEmail)}
                        onMoveLeft={() => moveApprover(i, -1)}
                        onMoveRight={() => moveApprover(i, 1)}
                        canMoveLeft={i > 0}
                        canMoveRight={i < approvers.length - 1}
                      />
                    ))}
                    {approvers.length === 0 && <span style={{ fontSize: 12.5, color: '#aaa9b7', padding: '4px 6px' }}>결재자를 추가하세요</span>}
                  </div>
                  <div ref={approverDropRef} style={{ position: 'relative' }}>
                    <input placeholder="이름 또는 이메일로 검색하여 결재자 추가" value={approverSearch}
                      onChange={e => { setApproverSearch(e.target.value); setShowApproverList(true); }}
                      onFocus={() => setShowApproverList(true)}
                      autoComplete="off"
                      style={{ width: '100%', height: 40, padding: '0 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 140ms, box-shadow 140ms' }}
                      onFocusCapture={eaFocusOn} onBlurCapture={eaFocusOff} />
                    {showApproverList && (
                      <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200, background: 'white', borderRadius: 10, boxShadow: '0 12px 28px -8px rgba(60,50,120,.22)', border: '1px solid #ececf2', padding: 4, maxHeight: 200, overflowY: 'auto' }}>
                        {filteredMembers.length === 0
                          ? <div style={{ padding: '10px 12px', fontSize: 13, color: '#aaa9b7', textAlign: 'center' }}>{members.length === 0 ? '조직원 정보를 불러오는 중...' : '검색 결과가 없습니다.'}</div>
                          : filteredMembers.map(m => memberDropItem(m, () => addApprover(m)))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 신청자 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>신청자</label>
                  <div style={{ height: 40, padding: '0 14px', borderRadius: 10, background: '#f4f3f9', border: '1.5px solid transparent', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: '#1f1d2e' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`, color: 'white', fontSize: 10, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{(user?.userName || '').slice(-2)}</div>
                    <span style={{ fontWeight: 600 }}>{user?.userName || '-'}</span>
                  </div>
                </div>

                {/* 휴가 구분 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>휴가 구분 <span style={{ color: '#e35d6a' }}>*</span></label>
                  <select value={form.leaveType} onChange={e => setForm(f => ({ ...f, leaveType: e.target.value }))}
                    style={{ width: '100%', height: 40, padding: '0 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 140ms, box-shadow 140ms' }}
                    onFocus={eaFocusOn} onBlur={eaFocusOff}>
                    {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                {/* 날짜 */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: workingDays !== null ? 10 : 18 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>시작일 <span style={{ color: '#e35d6a' }}>*</span></label>
                    <input type="date" value={form.startDt} onChange={e => setForm(f => ({ ...f, startDt: e.target.value }))}
                      style={{ width: '100%', height: 40, padding: '0 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums', transition: 'border-color 140ms, box-shadow 140ms' }}
                      onFocus={eaFocusOn} onBlur={e => { eaFocusOff(e); if (e.target.value) setForm(f => ({ ...f, endDt: f.endDt || e.target.value })); }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>종료일 <span style={{ color: '#e35d6a' }}>*</span></label>
                    <input type="date" value={form.endDt} onChange={e => setForm(f => ({ ...f, endDt: e.target.value }))}
                      style={{ width: '100%', height: 40, padding: '0 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums', transition: 'border-color 140ms, box-shadow 140ms' }}
                      onFocus={eaFocusOn} onBlur={eaFocusOff} />
                  </div>
                </div>

                {workingDays !== null && (
                  <div style={{ marginBottom: 18, padding: '10px 12px', borderRadius: 9, background: workingDays >= 0 ? hexToRgba(MINT, .10) : hexToRgba('#c44569', .08), border: `1px solid ${workingDays >= 0 ? hexToRgba(MINT, .25) : 'rgba(196,69,105,.20)'}`, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#1f1d2e' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={workingDays >= 0 ? MINT : '#c44569'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                    {workingDays >= 0
                      ? <span>총 <b style={{ color: MINT, fontWeight: 700 }}>{workingDays}일</b><span style={{ color: '#8b8a99', marginLeft: 6 }}>· 주말·공휴일 제외</span></span>
                      : <span style={{ color: '#c44569' }}>종료일이 시작일보다 앞에 있습니다.</span>}
                  </div>
                )}

                {/* 사유 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>사유 <span style={{ color: '#e35d6a' }}>*</span></label>
                  <textarea rows={3} placeholder="휴가 사유를 입력하세요" value={form.reason}
                    onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.55, minHeight: 80, boxSizing: 'border-box', transition: 'border-color 140ms, box-shadow 140ms' }}
                    onFocus={eaFocusOn} onBlur={eaFocusOff} />
                </div>

                {/* 업무 대행자 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>업무 대행자</label>
                  <div ref={agentDropRef} style={{ position: 'relative' }}>
                    {form.agentEmail ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', height: 40, border: '1.5px solid #ececf2', borderRadius: 10, background: '#f4f3f9' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                          {(members.find(m => m.userEmail === form.agentEmail)?.userName || '?').slice(-2)}
                        </div>
                        <span style={{ fontWeight: 600, color: '#1f1d2e', fontSize: 13.5, flex: 1 }}>{members.find(m => m.userEmail === form.agentEmail)?.userName || form.agentEmail}</span>
                        <button type="button" onClick={() => { setForm(f => ({ ...f, agentEmail: '' })); setAgentSearch(''); }}
                          style={{ width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'transparent', color: '#aaa9b7', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#e35d6a'} onMouseLeave={e => e.currentTarget.style.color = '#aaa9b7'}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <input placeholder="이름 또는 이메일로 검색" value={agentSearch}
                          onChange={e => { setAgentSearch(e.target.value); setShowAgentList(true); }}
                          onFocus={() => setShowAgentList(true)}
                          autoComplete="off"
                          style={{ width: '100%', height: 40, padding: '0 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 140ms, box-shadow 140ms' }}
                          onFocusCapture={eaFocusOn} onBlurCapture={eaFocusOff} />
                        {showAgentList && (
                          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200, background: 'white', borderRadius: 10, boxShadow: '0 12px 28px -8px rgba(60,50,120,.22)', border: '1px solid #ececf2', padding: 4, maxHeight: 200, overflowY: 'auto' }}>
                            {filteredAgents.length === 0
                              ? <div style={{ padding: '10px 12px', fontSize: 13, color: '#aaa9b7', textAlign: 'center' }}>{members.length === 0 ? '조직원 정보를 불러오는 중...' : '검색 결과가 없습니다.'}</div>
                              : filteredAgents.map(m => memberDropItem(m, () => { setForm(f => ({ ...f, agentEmail: m.userEmail })); setAgentSearch(''); setShowAgentList(false); }))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* 긴급 연락처 + 관계 */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14, marginBottom: 18 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>긴급 연락처</label>
                    <input type="tel" placeholder="010-0000-0000" value={form.emergencyContact}
                      onChange={e => setForm(f => ({ ...f, emergencyContact: e.target.value }))}
                      style={{ width: '100%', height: 40, padding: '0 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums', transition: 'border-color 140ms, box-shadow 140ms' }}
                      onFocus={eaFocusOn} onBlur={eaFocusOff} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>관계</label>
                    <input type="text" placeholder="예) 배우자, 부모" value={form.emergencyRelation}
                      onChange={e => setForm(f => ({ ...f, emergencyRelation: e.target.value }))}
                      style={{ width: '100%', height: 40, padding: '0 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 140ms, box-shadow 140ms' }}
                      onFocus={eaFocusOn} onBlur={eaFocusOff} />
                  </div>
                </div>

                {/* 비고 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>비고</label>
                  <textarea rows={2} placeholder="기타 사항 (선택)" value={form.remark}
                    onChange={e => setForm(f => ({ ...f, remark: e.target.value }))}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.55, minHeight: 70, boxSizing: 'border-box', transition: 'border-color 140ms, box-shadow 140ms' }}
                    onFocus={eaFocusOn} onBlur={eaFocusOff} />
                </div>

                {/* 알람 설정 */}
                <div>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#1f1d2e', marginBottom: 8 }}>알람 설정</label>
                  <div style={{ borderRadius: 10, border: '1.5px solid #ececf2', background: 'white', overflow: 'hidden' }}>
                    <NotifRow who="신청자" accent={PURPLE} bgTint={hexToRgba(PURPLE, .04)}
                      options={[{ key: 'appStatus', label: '결재 상태 알람' }, { key: 'appDayBefore', label: '일정 하루 전 알람' }]}
                      notif={notiForm} setNotif={setNotiForm} />
                    <NotifRow who="조직원" accent={MINT} bgTint={hexToRgba(MINT, .06)}
                      options={[{ key: 'orgApprover', label: '결재자 알람' }]}
                      notif={notiForm} setNotif={setNotiForm} />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '14px 22px', borderTop: '1px solid #ececf2', background: 'white' }}>
              <button onClick={() => setShowModal(false)} disabled={saving || submitting} style={{ height: 38, padding: '0 16px', borderRadius: 9, background: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>취소</button>
              <button onClick={handleSave} disabled={saving || submitting} style={{ height: 38, padding: '0 16px', borderRadius: 9, border: `1.5px solid ${hexToRgba(PURPLE, .35)}`, background: hexToRgba(PURPLE, .06), color: PURPLE, fontSize: 13, fontWeight: 600, cursor: (saving || submitting) ? 'default' : 'pointer', fontFamily: 'inherit', opacity: (saving || submitting) ? 0.6 : 1 }}>{saving ? '저장 중...' : '임시 저장'}</button>
              <button onClick={handleSubmit} disabled={submitting || saving} style={{ height: 38, padding: '0 22px', borderRadius: 9, border: 'none', background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`, color: 'white', fontSize: 13, fontWeight: 600, cursor: (submitting || saving) ? 'default' : 'pointer', fontFamily: 'inherit', boxShadow: `0 8px 16px -8px ${hexToRgba(PURPLE, .6)}`, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: (submitting || saving) ? 0.6 : 1 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                {submitting ? '제출 중...' : '제출'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
